import { ArrowUpRight, Cable, Wind, Flame, GitMerge, Settings, Layers } from "lucide-react";

const PRODUCT_CABLES = "https://customer-assets-m6fa6gv7.emergentagent.net/job_maine-web/artifacts/zx3f58fz_WhatsApp%20Image%202026-06-27%20at%203.32.29%20PM%20%281%29.jpeg";
const PRODUCT_HOSES = "https://customer-assets-m6fa6gv7.emergentagent.net/job_maine-web/artifacts/cvoxjxxo_WhatsApp%20Image%202026-06-27%20at%203.32.32%20PM.jpeg";

const OTHER_PRODUCTS = [
  {
    icon: Wind,
    title: "Oxygen Welding Hoses",
    desc: "High-pressure rubber hoses engineered for oxygen delivery in cutting and welding operations.",
    image: "https://images.unsplash.com/photo-1564182910174-53c0e19cb519?crop=entropy&cs=srgb&fm=jpg&q=85&w=800&h=500&fit=crop",
  },
  {
    icon: Flame,
    title: "LPG / Acetylene Hoses",
    desc: "Fuel gas hoses built with reinforced synthetic rubber for consistent flame quality.",
    image: "https://images.pexels.com/photos/5691692/pexels-photo-5691692.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    icon: GitMerge,
    title: "Twin Welding Hoses",
    desc: "Twin-line hoses for simultaneous oxygen and fuel gas supply with color-coded identification.",
    image: "https://customer-assets-m6fa6gv7.emergentagent.net/job_maine-web/artifacts/2njo2sp6_WhatsApp%20Image%202026-06-27%20at%204.11.16%20PM.jpeg",
  },
  {
    icon: Layers,
    title: "Industrial Rubber Hoses",
    desc: "Heavy-duty rubber hoses for water, air, chemicals and multipurpose industrial use.",
    image: "https://images.unsplash.com/photo-1598023707207-276835c2b5fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=800&h=500&fit=crop",
  },
  {
    icon: Settings,
    title: "Custom Industrial Solutions",
    desc: "Custom lengths, sizes and specifications engineered to match your specific application.",
    image: "https://images.unsplash.com/photo-1564182998523-6923112e7d6b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800&h=500&fit=crop",
  },
];

export const Products = () => {
  return (
    <section
      id="products"
      data-testid="products-section"
      className="relative bg-[#0B0E12] py-24 md:py-32 border-t border-[#F5F1EA]/8"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-brand-red" />
              <span data-testid="products-eyebrow" className="text-[11px] uppercase tracking-[0.35em] text-brand-red font-medium font-sans">
                Our Products
              </span>
            </div>
            <h2
              data-testid="products-heading"
              className="font-display tracking-tight text-white text-3xl md:text-4xl lg:text-5xl leading-[1.1] max-w-3xl font-medium"
            >
              Engineered for <span className="italic text-brand-red font-normal">Heavy-Duty</span> <br />
              Performance.
            </h2>
          </div>
          <p className="text-sm md:text-base text-white/55 max-w-md font-sans">
            A complete range of welding cables and rubber hoses — manufactured to meet the demanding standards of modern industry.
          </p>
        </div>

        {/* Featured product: Welding Cables */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div
            data-testid="product-card-cables"
            className="lg:col-span-12 group relative bg-[#161A21] border border-[#F5F1EA]/10 overflow-hidden hover:border-brand-red/50 transition-colors"
          >
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="relative aspect-[4/3] md:aspect-auto md:h-full min-h-[320px] overflow-hidden">
                <img
                  src={PRODUCT_CABLES}
                  alt="Welding Cables"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/70" />
              </div>

              <div className="p-8 md:p-12 flex flex-col justify-between gap-8 bg-[#13171E]">
                <div>
                  <div className="flex items-center gap-4 mb-6">
                    <Cable className="text-brand-red" size={32} />
                    <span className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-sans font-medium">Flagship Product</span>
                  </div>
                  <h3 className="font-display tracking-tight text-white text-3xl md:text-4xl leading-tight font-medium">
                    Welding <span className="italic text-brand-red font-normal">Cables</span>
                  </h3>
                  <p className="mt-5 text-sm text-white/60 leading-relaxed font-sans">
                    High-flexibility welding cables drawn with premium electrolytic copper and EC-grade aluminium conductors. Built for arc welding, MIG/MAG operations, and heavy industrial fabrication.
                  </p>

                  <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      { tag: "Copper", desc: "Conductor" },
                      { tag: "Aluminium", desc: "Conductor" },
                      { tag: "Flexible", desc: "Stranding" },
                      { tag: "TRS", desc: "Insulation" },
                      { tag: "HOFR", desc: "Insulation" },
                      { tag: "EPR", desc: "Insulation" },
                    ].map((s, i) => (
                      <div
                        key={i}
                        data-testid={`cable-spec-${i}`}
                        className="border border-white/10 px-3 py-2"
                      >
                        <div className="text-sm font-semibold text-white uppercase tracking-wide font-sans">{s.tag}</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-white/45 mt-0.5 font-sans">{s.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <a
                  href="#contact"
                  data-testid="cable-cta"
                  className="inline-flex items-center justify-between gap-2 border-t border-white/15 pt-5 text-xs uppercase tracking-[0.25em] font-semibold text-white hover:text-brand-red transition-colors group/cta font-sans"
                >
                  Enquire Now
                  <ArrowUpRight size={18} className="group-hover/cta:translate-x-1 group-hover/cta:-translate-y-1 transition-transform" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Grid of other products */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Highlight Hoses tile with image */}
          <div
            data-testid="product-card-hoses-highlight"
            className="group relative bg-[#161A21] border border-[#F5F1EA]/10 overflow-hidden hover:border-brand-red/50 transition-colors flex flex-col md:col-span-2 lg:col-span-1"
          >
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src={PRODUCT_HOSES}
                alt="Industrial Rubber Hoses"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/70" />
            </div>
            <div className="p-8 flex-1 flex flex-col justify-between gap-5">
              <div>
                <Wind className="text-brand-red mb-4" size={28} />
                <h3 className="font-display tracking-tight text-white text-2xl leading-tight font-medium">
                  Complete Range of <span className="italic text-brand-red font-normal">Rubber Hoses</span>
                </h3>
                <p className="mt-3 text-sm text-white/60 leading-relaxed font-sans">
                  Reinforced industrial rubber hoses — pressure-tested for safety across welding and industrial gas operations.
                </p>
              </div>
              <a
                href="#contact"
                data-testid="hose-cta"
                className="inline-flex items-center justify-between gap-2 border-t border-white/15 pt-4 text-xs uppercase tracking-[0.25em] font-semibold text-white hover:text-brand-red transition-colors group/cta font-sans"
              >
                Enquire Now
                <ArrowUpRight size={16} className="group-hover/cta:translate-x-1 group-hover/cta:-translate-y-1 transition-transform" />
              </a>
            </div>
          </div>

          {/* Smaller product cards */}
          {OTHER_PRODUCTS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={i}
                data-testid={`product-card-${i}`}
                className="group relative bg-[#161A21] border border-[#F5F1EA]/10 hover:border-brand-red/50 hover:-translate-y-1 transition-all duration-300 flex flex-col overflow-hidden"
              >
                {/* Product image */}
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#161A21] via-[#161A21]/40 to-transparent" />
                  <div className="absolute top-4 left-4 p-2.5 bg-[#0E1116]/90 backdrop-blur-sm border border-brand-red/30">
                    <Icon size={18} className="text-brand-red" strokeWidth={1.5} />
                  </div>
                  <span className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.3em] text-white/50 font-sans font-medium">
                    0{i + 2}
                  </span>
                </div>

                {/* Content */}
                <div className="p-7 flex-1 flex flex-col justify-between gap-5">
                  <div>
                    <h4 className="font-display tracking-tight text-white text-xl md:text-2xl leading-tight font-medium mb-3">
                      {p.title}
                    </h4>
                    <p className="text-sm text-white/55 leading-relaxed font-sans">
                      {p.desc}
                    </p>
                  </div>
                  <a
                    href="#contact"
                    data-testid={`product-${i}-cta`}
                    className="inline-flex items-center justify-between gap-2 border-t border-white/10 pt-4 text-[11px] uppercase tracking-[0.25em] font-semibold text-white/70 group-hover:text-brand-red transition-colors font-sans"
                  >
                    Enquire Now
                    <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
